'use client';

import React from 'react';
import View from '../view';
import PreviewImage from '../cards/preview-image';
import {
  Button,
  Card,
  CardBody,
  Image,
  Modal,
  ModalBody,
  ModalContent,
  ModalHeader,
  useDisclosure,
} from '@nextui-org/react';
import {
  ChevronLeftIcon,
  ChevronRightIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import { Gallery } from '@/typings/projects';
import NextImage from 'next/image';

type Props = {
  items: Gallery[];
};

const ImageGallery = ({ items }: Props) => {
  const { isOpen, onOpen, onOpenChange } = useDisclosure();
  const [currentImage, setCurrentImage] = React.useState(items[0]);

  const onSelectImage = React.useCallback(
    (value: Gallery) => {
      setCurrentImage(value);
      onOpen();
    },
    [onOpen]
  );

  const onPreviosSlide = React.useCallback(() => {
    const currentIndex = items.findIndex((item) => item === currentImage);
    const prevIndex = currentIndex === 0 ? items.length - 1 : currentIndex - 1;
    setCurrentImage(items[prevIndex]);
  }, [currentImage, items]);

  const onNextSlide = React.useCallback(() => {
    const currentIndex = items.findIndex((item) => item === currentImage);
    const nextIndex = currentIndex === items.length - 1 ? 0 : currentIndex + 1;
    setCurrentImage(items[nextIndex]);
  }, [currentImage, items]);

  return (
    <React.Fragment>
      <View className="my-10 grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4 sm:gap-8">
        {items?.map((item, index) => {
          return (
            <PreviewImage
              removeWrapper
              key={index}
              src={String(item?.small)}
              alt={String(item?.alt)}
              title={String(item?.name)}
              onClick={() => onSelectImage(item)}
              classNames={{
                wrapper: 'h-full',
                img: 'h-full w-full object-cover max-h-96',
              }}
              className="object-cover"
            />
          );
        })}
        {/*Slider Model  */}
      </View>
      <Modal
        size="full"
        isOpen={isOpen}
        onOpenChange={onOpenChange}
        isDismissable
      >
        <ModalContent className="bg-opacity-90 backdrop-blur-sm">
          {(onClose) => (
            <>
              <ModalHeader className="flex items-center justify-between gap-1 sm:px-10">
                <View />
                <View>
                  {currentImage?.name ? (
                    <h4 className="line-clamp-1 truncate">
                      {currentImage?.name}
                    </h4>
                  ) : (
                    'Greenconceptors Image'
                  )}
                </View>
                <View>
                  <Button onPress={onClose} size="lg" isIconOnly variant="flat">
                    <XMarkIcon className="h-6 w-6" />
                  </Button>
                </View>
              </ModalHeader>
              <ModalBody className="items-center justify-center">
                <View className="flex gap-2 w-full justify-between">
                  <View className="flex items-center justify-center">
                    <Card
                      isPressable
                      shadow="none"
                      onPress={onPreviosSlide}
                      className="bg-opacity-50 rounded-full "
                    >
                      <CardBody className="sm:h-24 sm:w-24 flex items-center justify-center">
                        <ChevronLeftIcon className="h-8 w-8 sm:h-10 sm:w-10" />
                      </CardBody>
                    </Card>
                  </View>
                  <Image
                    as={NextImage}
                    src={String(currentImage?.original || currentImage?.small)}
                    alt={String(currentImage.alt)}
                    title={String(currentImage.name)}
                    radius="sm"
                    height={420}
                    width={420}
                  />
                  <View className="flex items-center justify-center">
                    <Card
                      isPressable
                      shadow="none"
                      onPress={onNextSlide}
                      className="bg-opacity-50 rounded-full "
                    >
                      <CardBody className="sm:h-24 sm:w-24 flex items-center justify-center">
                        <ChevronRightIcon className="h-8 w-8 sm:h-10 sm:w-10" />
                      </CardBody>
                    </Card>
                  </View>
                </View>
              </ModalBody>
            </>
          )}
        </ModalContent>
      </Modal>
    </React.Fragment>
  );
};

export default ImageGallery;
